/**
 * UpgradeGuideSection — 모듈 상세 '업그레이드 시 확인사항' 섹션 (queue #692
 * §UAT-2026-07-14-R692 §R692-3 · §R665 `ConfigSection`·§R666 `TroubleshootingSection`·
 * §R672 `ChangeHistorySection` 계보).
 *
 * `ModuleDetailBody` 에 삽입되는 노드 상세 공유 섹션 — `ChangeHistorySection` 바로 위.
 * 데이터는 §R643 `module-maturity.json`(commits[].breaking · versionTimeline[]) 과 신규
 * 수기 큐레이션 `module-breaking-changes.json` 을 `selectUpgradeImpact` 로 조합한다(BE 0 ·
 * 정적 import · 망 호출 0). 대상 artifact 이력 부재/`gitAvailable:false`/commits 0건이면
 * 섹션 전체를 숨긴다(빈 표 미노출 — §R665/§R666/§R672 0건 숨김 계보).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModuleNode, ModuleMaturity, ModuleBreakingChanges } from '../types/showcase';
import { selectUpgradeImpact } from '../data/upgrade-impact';
import moduleMaturityJson from '../data/module-maturity.json';
import moduleBreakingChangesJson from '../data/module-breaking-changes.json';

const MODULE_MATURITY = moduleMaturityJson as unknown as ModuleMaturity;
const MODULE_BREAKING_CHANGES = moduleBreakingChangesJson as unknown as ModuleBreakingChanges;

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '0 0 var(--spacing-3)',
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 'var(--spacing-3)',
  marginBottom: 'var(--spacing-3)',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  fontFamily: 'var(--font-family-mono)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
};

const latestStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

const safeBoxStyle: React.CSSProperties = {
  border: '1px solid var(--color-success-border, var(--color-surface-border))',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
};

const safeTitleStyle: React.CSSProperties = {
  margin: '0 0 var(--spacing-1)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  color: 'var(--color-success-text, var(--color-text-primary))',
};

const safeDetailStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const warnBoxStyle: React.CSSProperties = {
  border: '1px solid var(--color-warning-border, var(--color-surface-border))',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
};

const warnTitleStyle: React.CSSProperties = {
  margin: '0 0 var(--spacing-2)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  color: 'var(--color-warning-text, var(--color-text-primary))',
};

const itemBlockStyle: React.CSSProperties = {
  borderTop: '1px solid var(--color-surface-border)',
  padding: 'var(--spacing-2) 0',
};

const itemMetaStyle: React.CSSProperties = {
  margin: '0 0 var(--spacing-1)',
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

const itemRowGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 'var(--spacing-1) var(--spacing-3)',
  margin: 0,
  fontSize: 'var(--font-size-xs)',
};

const itemDtStyle: React.CSSProperties = { color: 'var(--color-text-muted)', fontWeight: 600 };
const itemDdStyle: React.CSSProperties = { margin: 0, color: 'var(--color-text-secondary)' };

const otherChangesStyle: React.CSSProperties = {
  marginTop: 'var(--spacing-2)',
  paddingTop: 'var(--spacing-2)',
  borderTop: '1px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

interface Props {
  node: ModuleNode;
  /** 테스트용 fixture 오버라이드 — 기본은 정적 import(ChangeHistorySection/CompatibilityMatrix 와 동일 SSOT). */
  maturity?: ModuleMaturity;
  breakingCuration?: ModuleBreakingChanges;
}

export function UpgradeGuideSection({ node, maturity = MODULE_MATURITY, breakingCuration = MODULE_BREAKING_CHANGES }: Props) {
  const { t } = useTranslation();
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  const impact = selectUpgradeImpact(maturity, breakingCuration, node.artifact, selectedVersion);

  // §R692-3(d) empty — 대상 artifact 이력 부재/gitAvailable:false/commits 0건 → 섹션 전체 숨김.
  if (!impact) return null;

  const entry = maturity.modules?.[node.artifact];
  const timeline = entry?.versionTimeline ?? [];

  return (
    <section aria-labelledby="upgrade-guide-section-title" style={{ marginBottom: 'var(--spacing-4)' }}>
      <h3 id="upgrade-guide-section-title" style={sectionTitleStyle}>
        {t('showcase.upgrade.title')}
      </h3>

      <div style={headerRowStyle}>
        {!impact.usingFallback && (
          <label style={labelStyle} htmlFor="upgrade-guide-version-select">
            <span>{t('showcase.upgrade.myVersion')}</span>
            <select
              id="upgrade-guide-version-select"
              style={selectStyle}
              value={impact.fromVersion ?? ''}
              onChange={(e) => setSelectedVersion(e.target.value)}
            >
              {timeline.map((point) => (
                <option key={`${point.version}-${point.date}`} value={point.version}>
                  {point.version}
                </option>
              ))}
            </select>
          </label>
        )}
        {impact.latestVersion && (
          <span style={latestStyle}>{t('showcase.upgrade.latest', { version: impact.latestVersion })}</span>
        )}
      </div>

      {impact.usingFallback && (
        <p style={{ ...latestStyle, marginBottom: 'var(--spacing-2)' }}>{t('showcase.upgrade.allChanges')}</p>
      )}

      {!impact.hasBreaking ? (
        <div style={safeBoxStyle} data-testid="upgrade-guide-safe">
          <p style={safeTitleStyle}>✅ {t('showcase.upgrade.safe')}</p>
          <p style={safeDetailStyle}>{t('showcase.upgrade.safeDetail', { count: impact.totalChanges })}</p>
        </div>
      ) : (
        <div style={warnBoxStyle} data-testid="upgrade-guide-breaking">
          <p style={warnTitleStyle}>⚠ {t('showcase.upgrade.breaking', { count: impact.breakingItems.length })}</p>

          {impact.breakingItems.map((item, idx) => (
            <div key={`${item.hash ?? ''}-${item.date}-${idx}`} style={itemBlockStyle}>
              <p style={itemMetaStyle}>
                <code>{item.date}</code>
                {item.hash && (
                  <>
                    {' · '}
                    <code>{item.hash.slice(0, 8)}</code>
                  </>
                )}
              </p>
              <dl style={itemRowGridStyle}>
                <dt style={itemDtStyle}>{t('showcase.upgrade.col.change')}</dt>
                <dd style={itemDdStyle}>{item.summary}</dd>
                {item.hasDetail ? (
                  <>
                    <dt style={itemDtStyle}>{t('showcase.upgrade.col.affected')}</dt>
                    <dd style={itemDdStyle}>{item.affectedApis.length > 0 ? item.affectedApis.join(', ') : '—'}</dd>
                    <dt style={itemDtStyle}>{t('showcase.upgrade.col.action')}</dt>
                    <dd style={itemDdStyle}>{item.action}</dd>
                  </>
                ) : (
                  <dd style={{ ...itemDdStyle, gridColumn: '1 / -1' }}>{t('showcase.upgrade.noDetail')}</dd>
                )}
              </dl>
            </div>
          ))}

          <p style={otherChangesStyle}>
            {t('showcase.upgrade.otherChanges', { count: impact.changesAfterLastBreaking })}
          </p>
        </div>
      )}
    </section>
  );
}
